import { LinkInterface } from '../types/drupal';
import getLinkPath from './getLinkPath';

interface GetDrupalMenuQuery {
  drupal: {
    menuLinkContentQuery: {
      entities?: LinkInterface[];
    };
  };
  chartDrupal?: {
    menuLinkContentQuery: {
      entities?: LinkInterface[];
    };
  };
}

export interface MenuLink extends LinkInterface {
  links: MenuLink[];
}

const getI18nEntities = (entities: LinkInterface[], langPrefix: string) =>
  entities
    .filter(({ entityTranslations }) =>
      entityTranslations?.some(
        ({ entityLanguage, fieldDisableLanguage }) =>
          langPrefix === entityLanguage!.id && !fieldDisableLanguage
      )
    )
    .map(({ entityTranslations, link, ...rest }) => {
      const entityTranslation = entityTranslations?.find(
        ({ entityLanguage }) => langPrefix === entityLanguage!.id
      )!;

      const path = getLinkPath(link!.url, langPrefix);

      return { ...rest, ...entityTranslation, link: { url: { path } } };
    });

const getDrupalMenu = (query: GetDrupalMenuQuery, langPrefix: string) => {
  const queryData = query.chartDrupal || query.drupal;
  const entities = getI18nEntities(
    queryData.menuLinkContentQuery!.entities as LinkInterface[],
    langPrefix
  );

  const menu = new Map<string, MenuLink>();
  const parentNotFound: MenuLink[] = [];
  const lostChildren: MenuLink[] = [];

  const processLink = (
    { entityUuid, parent, ...rest }: any,
    errorOnNoParent?: boolean
  ) => {
    if (parent === null) {
      menu.set(`menu_link_content:${entityUuid}`, {
        links: [],
        ...rest
      } as MenuLink);
    } else {
      let parentObj = menu.get(parent!);

      // check children on second run through if needed
      if (!parentObj && errorOnNoParent) {
        menu.forEach(topLevel => {
          if (!parentObj && topLevel.links.length) {
            parentObj = topLevel.links.find(link => {
              return `menu_link_content:${link.entityUuid}` === parent;
            });
          }
        });
      }

      // now check if we got anything from checking the children
      if (!parentObj && errorOnNoParent) {
        // No parent found on any run through.
        // Add to array and break the build so we can fix
        lostChildren.push({entityUuid, parent, ...rest});
      } else if (!parentObj) {
        parentNotFound.push({ entityUuid, parent, ...rest });
      } else {
        parentObj.links.push({ entityUuid, links: [], ...rest } as MenuLink);
      }
    }
  };

  entities.forEach(entity => processLink(entity));
  parentNotFound.forEach(entity => processLink(entity, true));

  // if there are any lost children break the build and list them for fixing.
  if (lostChildren.length > 0) {
    lostChildren.forEach(link => console.info('lost child link info', {
      id: link.entityId,
      langCode: link.entityLanguage?.id,
      menu: link.entityBundle
    }));
    throw new Error('Menu item(s) with invalid parents listed above');
  }

  return Array.from(menu.values());
};

export default getDrupalMenu;
